/**
 * TextCorrectionService - 텍스트 수정 관련 서비스
 * Single Responsibility: 텍스트 수정 로직 캡슐화
 * 
 * NOTE: 기존 textCorrection.ts의 함수들을 래핑하여 사용
 * 기존 로직을 변경하지 않고 서비스 레이어로 감싸는 것이 목적
 */

import type { GrammarError } from '$lib/types/grammar'
import {
  findTextPosition,
  applyCorrection as applyTextCorrection,
  updateErrorPositions as updateTextErrorPositions,
  updateErrorsForText as updateTextErrorsForText,
  applyAllCorrections as applyAllTextCorrections
} from '$lib/utils/textCorrection'

export class TextCorrectionService {
  /**
   * 텍스트에서 특정 문자열의 위치 찾기
   * 기존 findTextPosition 함수 래핑
   */
  static findPosition(
    text: string,
    searchText: string,
    expectedStart: number,
    searchRadius = 50
  ): { start: number; end: number } | null {
    return findTextPosition(text, searchText, expectedStart, searchRadius)
  }

  /**
   * 단일 수정 적용
   * 기존 applyCorrection 함수 래핑
   */
  static applyCorrection(
    text: string,
    originalText: string,
    replacementText: string,
    expectedStart: number
  ): { newText: string; offset: number } | null {
    // 빈 텍스트 체크
    if (!text) {
      console.warn('[TextCorrectionService] Empty text provided')
      return null
    }
    
    return applyTextCorrection(text, originalText, replacementText, expectedStart)
  }

  /**
   * 수정 후 에러 위치 업데이트
   * 기존 updateErrorPositions 함수 래핑
   */
  static updateErrorPositions(
    errors: GrammarError[],
    correctionStart: number,
    offset: number
  ): GrammarError[] {
    // 빈 배열 체크
    if (!errors || errors.length === 0) {
      return []
    }
    
    return updateTextErrorPositions(errors, correctionStart, offset)
  }

  /**
   * 텍스트 변경 시 에러 업데이트
   * 기존 updateErrorsForText 함수 래핑
   */
  static updateErrorsForText(
    errors: GrammarError[],
    newText: string
  ): GrammarError[] {
    // 빈 배열이나 텍스트 체크
    if (!errors || errors.length === 0) {
      return []
    }
    
    if (!newText) {
      // 텍스트가 비어있으면 모든 에러 제거
      return []
    }
    
    return updateTextErrorsForText(errors, newText)
  }

  /**
   * 모든 수정 적용
   * 기존 applyAllCorrections 함수 래핑
   */
  static applyAllCorrections(
    text: string,
    errors: GrammarError[]
  ): { newText: string; appliedCount: number } {
    // 빈 텍스트나 에러 체크
    if (!text || !errors || errors.length === 0) {
      return { newText: text || '', appliedCount: 0 }
    }
    
    return applyAllTextCorrections(text, errors)
  }
  
  /**
   * 모든 수정 적용 (위치 오프셋 정보 포함)
   */
  static applyAllCorrectionsWithOffsets(
    text: string,
    errors: GrammarError[]
  ): { newText: string; appliedCount: number; positionOffsets: Map<number, number> } {
    // 빈 텍스트나 에러 체크
    if (!text || !errors || errors.length === 0) {
      return { newText: text || '', appliedCount: 0, positionOffsets: new Map() }
    }
    
    // Sort errors from end to start to avoid position shifts
    const sortedErrors = [...errors]
      .filter(error => error.corrections.length > 0 && error.originalText)
      .sort((a, b) => {
        const aPos = (a as any).startPos ?? a.position?.start ?? 0
        const bPos = (b as any).startPos ?? b.position?.start ?? 0
        return bPos - aPos
      })
    
    let newText = text
    let appliedCount = 0
    const positionOffsets = new Map<number, number>()
    
    for (const error of sortedErrors) {
      // Check if AI suggests keeping original or user marked as kept-original
      if (error.isOriginalKept || error.userAction === 'kept-original' || 
          (error.aiComment && !error.aiComment.agreesWithCorrection)) {
        // Skip this correction (keep original)
        continue
      }
      
      const correction = error.corrections[0]
      if (!correction || !error.originalText) continue
      
      // Handle both old format (startPos) and new format (position.start)
      const startPos = (error as any).startPos ?? error.position?.start ?? 0
      
      const result = this.applyCorrection(
        newText,
        error.originalText,
        correction.text,
        startPos
      )
      
      if (result) {
        newText = result.newText
        positionOffsets.set(startPos, result.offset)
        appliedCount++
      }
    }
    
    return { newText, appliedCount, positionOffsets }
  }

  /**
   * 수정 가능한 에러 필터링
   * (무시되지 않은 에러만)
   */
  static filterCorrectableErrors(
    errors: GrammarError[],
    ignoredIds: Set<string>
  ): GrammarError[] {
    if (!errors || errors.length === 0) {
      return []
    }
    
    return errors.filter(error => !ignoredIds.has(error.id))
  }
}